import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export function useFriends(profileId?: string) {
  const { profile } = useAuth();
  const targetId = profileId || profile?.id;

  const { data: friends = [], isLoading: isLoadingFriends } = useQuery({
    queryKey: ['friends', targetId],
    queryFn: async () => {
      if (!targetId) return [];
      const { data, error } = await supabase.rpc('get_mutual_follows', {
        _profile_id: targetId,
      });
      if (error) throw error;
      return (data || []).map((r: any) => r.friend_id as string);
    },
    enabled: !!targetId,
  });

  const { data: friendProfiles = [], isLoading: isLoadingProfiles } = useQuery({
    queryKey: ['friend-profiles', targetId],
    queryFn: async () => {
      if (!targetId) return [];
      const { data: friendIds, error: rpcError } = await supabase.rpc('get_mutual_follows', {
        _profile_id: targetId,
      });
      if (rpcError) throw rpcError;
      if (!friendIds || friendIds.length === 0) return [];

      const ids = friendIds.map((r: any) => r.friend_id);
      const { data, error } = await supabase
        .from('profiles_public')
        .select('*')
        .in('id', ids);
      if (error) throw error;
      return data || [];
    },
    enabled: !!targetId,
  });

  const isFriend = (userId: string) => friends.includes(userId);

  return {
    friends,
    friendProfiles,
    friendCount: friends.length,
    isFriend,
    isLoading: isLoadingFriends || isLoadingProfiles,
  };
}

export function useHeadToHead(profileA?: string, profileB?: string) {
  return useQuery({
    queryKey: ['head-to-head', profileA, profileB],
    queryFn: async () => {
      if (!profileA || !profileB) return null;
      const { data, error } = await supabase.rpc('get_head_to_head', {
        _profile_a: profileA,
        _profile_b: profileB,
      });
      if (error) throw error;
      return data?.[0] || { total_matches: 0, wins_a: 0, wins_b: 0, draws: 0 };
    },
    enabled: !!profileA && !!profileB,
  });
}

export function useIsFollowedBy(targetUserId?: string) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: ['is-followed-by', profile?.id, targetUserId],
    queryFn: async () => {
      if (!profile?.id || !targetUserId) return false;
      const { data, error } = await supabase
        .from('user_follows')
        .select('id')
        .eq('follower_id', targetUserId)
        .eq('following_id', profile.id)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: !!profile?.id && !!targetUserId,
  });
}
